import { useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const apiUrl = process.env.REACT_APP_API_URL;

interface CreatePaymentLinkBody {
	description: string;
	amount: number;
	name: string;
	email: string;
	redirect_url: string;
}

interface CreatePaymentLinkResponse {
	payment_method?: {
		url?: string;
	};
}

interface RawCreatePaymentLinkApiResponse {
	response?: {
		data?: {
			payment_url?: string;
		};
	};
}

const normalizePaymentLinkResponse = (
	raw: RawCreatePaymentLinkApiResponse
): CreatePaymentLinkResponse => {
	const normalizedUrl = raw?.response?.data?.payment_url || "";

	return {
		payment_method: {
			url: normalizedUrl,
		},
	};
};

const useCreatePaymentLink = () => {
	const { getAccessTokenSilently } = useAuth0();

	const [data, setData] = useState<CreatePaymentLinkResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const createPaymentLink = async (requestBody: CreatePaymentLinkBody) => {
		setLoading(true);
		setError(null);

		try {
			const token = await getAccessTokenSilently();
			const response = await axios.post<RawCreatePaymentLinkApiResponse>(
				`${apiUrl}/api/v1/transaction/link`,
				requestBody,
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const normalizedResponse = normalizePaymentLinkResponse(response.data);

			setData(normalizedResponse);
			return normalizedResponse;
		} catch (err: any) {
			const message = err?.response?.data?.message || "Error al generar el link de pago";
			setError(message);
			throw new Error(message);
		} finally {
			setLoading(false);
		}
	};

	const reset = () => {
		setData(null);
		setError(null);
		setLoading(false);
	};

	return { createPaymentLink, data, loading, error, reset };
};

export default useCreatePaymentLink;
